import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { updateLauraDeliverable } from "@/app/lib/auditflowApi";
import type { LauraStageId } from "@/app/components/shared/types";
import { stageLabel } from "./stages";

interface Props {
    auditId: string;
    stage: LauraStageId;
    content: string;
    outputLanguage: string;
    onSaved: (content: string) => void;
}

export function LauraDeliverablePanel({
    auditId,
    stage,
    content,
    outputLanguage,
    onSaved,
}: Props) {
    const [draft, setDraft] = useState(content);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(content);
    }, [content, stage]);

    async function save() {
        setSaving(true);
        try {
            await updateLauraDeliverable(auditId, stage, draft);
            onSaved(draft);
        } finally {
            setSaving(false);
        }
    }

    const lang = outputLanguage.startsWith("pt") ? "pt" : "en";

    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-medium text-gray-900">
                    {stageLabel(stage, lang)} — Deliverable
                </h3>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || draft === content}
                    className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                    {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                    Save
                </button>
            </div>
            <textarea
                className="flex-1 resize-none p-4 font-mono text-sm leading-relaxed outline-none"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Deliverable markdown will appear here. Use the chat to generate content, or edit directly."
            />
        </div>
    );
}
